import { DataModel, DataModelAttribute } from '@/types/DataModel';

import {
    DEFAULT_FILTER_OPERATOR,
    FilterAttributeOption,
    FilterGroup,
    FilterKeyword,
    FilterLogicalOperator,
    FilterRule,
    ItemsSearchFilterQuery,
} from './FilterBuilder.types';

const LOGICAL_OPERATORS_STRING = '(?<=\\s)(AND|OR)(?=\\s|$)';

export const ALLOWED_FILTER_WIDGET_TYPES = [
    'Enum',
    'EnumSet',
    'String',
    'Boolean',
    'Date',
    'DateTime',
    'SingleBoolean',
    'OpenEnum',
    'OpenEnumSet',
    'Codelist',
    'OpenCodelist',
    'CodelistSet',
    'OpenCodelistSet',
    'Integer',
    'Float',
    'Decimal',
] as const;

export function getAttributeWidgetType(
    attribute?: FilterAttributeOption | DataModelAttribute | string | null,
): string | null {
    if (!attribute || typeof attribute === 'string') {
        return null;
    }
    const template = 'template' in attribute ? attribute.template : undefined;
    const typeName = 'typeName' in attribute ? attribute.typeName : undefined;
    return (template || typeName || attribute.baseClass || null) as string | null;
}

export function isAllowedFilterWidget(
    attribute?: FilterAttributeOption | DataModelAttribute | string | null,
): boolean {
    const type = getAttributeWidgetType(attribute);
    return type != null && (ALLOWED_FILTER_WIDGET_TYPES as readonly string[]).includes(type);
}

export function isDateTimeAttribute(
    attribute?: FilterAttributeOption | DataModelAttribute | string | null,
): boolean {
    if (!attribute || typeof attribute === 'string') {
        return false;
    }
    return getAttributeWidgetType(attribute) === 'DateTime' || attribute.baseClass === 'DateTime';
}

export function isDataModelAttribute(
    attribute: FilterAttributeOption | DataModelAttribute | string | null | undefined,
): attribute is DataModelAttribute {
    return typeof attribute === 'object'
        && attribute !== null
        && ('baseClass' in attribute || 'typeName' in attribute || 'template' in attribute);
}

export function resolveRuleDataModelAttribute(
    rule: FilterRule,
    attributes: FilterAttributeOption[],
    dataModel?: DataModel,
): DataModelAttribute | null {
    const name = getAttributeName(rule.attribute);
    if (!name) {
        return null;
    }
    const fromModel = dataModel?.attribute?.(name);
    if (fromModel) {
        return fromModel;
    }
    const fromList = attributes.find((attribute) => attribute.name === name);
    if (isDataModelAttribute(fromList)) {
        return fromList;
    }
    if (isDataModelAttribute(rule.attribute)) {
        return rule.attribute;
    }
    return null;
}

export function prepareFilterKeyword(
    attribute: FilterAttributeOption | DataModelAttribute | string | null | undefined,
    keyword: FilterKeyword,
): FilterKeyword {
    if (keyword instanceof Date) {
        return keyword;
    }
    if (Array.isArray(keyword)) {
        return keyword;
    }
    if (keyword == null || keyword === '') {
        return '';
    }

    if (isDateTimeAttribute(attribute) && /^-?\d+$/.test(keyword.trim())) {
        return new Date(Number(keyword) * 1000);
    }

    const widgetType = getAttributeWidgetType(attribute);
    const isMultiValue = widgetType === 'EnumSet'
        || widgetType === 'OpenEnumSet'
        || widgetType === 'CodelistSet'
        || widgetType === 'OpenCodelistSet'
        || (typeof attribute === 'object' && attribute?.baseClass === 'Array');
    if (isMultiValue) {
        const parts = keyword.match(/(".*?"|[^"\s]+)(?=\s*|\s*$)/g);
        if (parts) {
            return parts.map((part) => part.replace(/"/g, '').trim()).filter(Boolean);
        }
    }

    return keyword;
}

export function createEmptyRule(): FilterRule {
    return {
        attribute: null,
        operator: DEFAULT_FILTER_OPERATOR,
        keyword: '',
        notOperator: false,
    };
}

export function createEmptyGroup(operator?: FilterLogicalOperator): FilterGroup {
    return {
        rules: [createEmptyRule()],
        operator,
        notOperator: false,
        ruleConnector: 'AND',
    };
}

export function createEmptyFilterGroups(): FilterGroup[] {
    return [createEmptyGroup()];
}

export function getAttributeName(attribute?: FilterAttributeOption | string | null): string {
    if (!attribute) {
        return '';
    }
    if (typeof attribute === 'string') {
        return attribute;
    }
    return attribute.name || '';
}

function isDoubleQuoted(value: FilterKeyword): boolean {
    return typeof value === 'string' && value.startsWith('"') && value.endsWith('"');
}

function isEmptyKeyword(keyword: FilterKeyword): boolean {
    if (keyword instanceof Date) {
        return false;
    }
    if (Array.isArray(keyword)) {
        return keyword.length === 0;
    }
    return keyword === '' || keyword == null;
}

function isRuleEmpty(rule: FilterRule): boolean {
    return !getAttributeName(rule.attribute) || isEmptyKeyword(rule.keyword);
}

function convertDateTimeValue(value: string): string {
    if (!Number.isNaN(Number(value)) && value.trim() !== '') {
        return value;
    }
    const dateTime = new Date(value);
    if (Number.isNaN(dateTime.getTime())) {
        return value;
    }
    dateTime.setSeconds(0, 0);
    return String(Math.floor(dateTime.getTime() / 1000));
}

function formatRuleValue(rule: FilterRule): string {
    let val: string;
    if (isDoubleQuoted(rule.keyword)) {
        val = String(rule.keyword);
    } else if (rule.keyword instanceof Date) {
        const dateTime = new Date(rule.keyword.getTime());
        dateTime.setSeconds(0, 0);
        val = `"${Math.floor(dateTime.getTime() / 1000)}"`;
    } else {
        val = `"${rule.keyword}"`;
    }

    const attribute = typeof rule.attribute === 'object' ? rule.attribute : null;
    if (isDateTimeAttribute(attribute) && typeof rule.keyword === 'string') {
        const inner = isDoubleQuoted(val) ? val.slice(1, -1) : val;
        val = `"${convertDateTimeValue(inner)}"`;
    }

    if (val.includes(' OR ') || val.includes(' AND ')) {
        val = `(${val})`;
    }
    return val;
}

export function serializeFilterGroups(groups: FilterGroup[]): ItemsSearchFilterQuery {
    let queryString = '';

    groups.forEach((group, index) => {
        const hasAllAttributesEmpty = group.rules.every(isRuleEmpty);
        if (hasAllAttributesEmpty) {
            return;
        }

        if (queryString !== '') {
            queryString += ` ${group.operator ?? 'OR'} `;
        } else if (index > 0) {
            queryString += ` ${group.operator ?? 'OR'} `;
        }

        if (group.notOperator) {
            queryString += 'NOT ';
        }
        queryString += '(';

        const filledRules = group.rules.filter((rule) => !isRuleEmpty(rule));
        filledRules.forEach((rule, ruleIndex) => {
            if (ruleIndex > 0) {
                const connector = rule.connector ?? group.ruleConnector ?? 'AND';
                queryString += ` ${connector} `;
            }
            if (rule.notOperator) {
                queryString += 'NOT ';
            }
            const attributeName = getAttributeName(rule.attribute);
            queryString += `${attributeName}${rule.operator || DEFAULT_FILTER_OPERATOR}${formatRuleValue(rule)}`;
        });

        queryString += ')';
    });

    return { keyword: queryString };
}

export function splitStringByOuterParentheses(str: string): string[] {
    const trimmed = (str || '').trim();
    if (!trimmed) {
        return [''];
    }
    const parts = trimmed.split(/(AND|OR)(?![^(]*\))/).filter((part) => part !== '');
    const mapped = parts.map((part) => {
        let value = part.trim();
        if (value.startsWith('(') && value.endsWith(')')) {
            value = value.substring(1, value.length - 1);
        }
        return value;
    });
    return mapped.length ? mapped : [trimmed];
}

/**
 * Live preview / URL keywords use `attr = "value"` (decodeItemQuery turns `:`
 * into ` = `). Saved profiles still store `attr:"value"`. Treat both as `:`.
 * Longer operators must win so `==` / `<=` / `>=` are not split on `=`.
 */
const PARSE_OPERATORS: Array<{ key: string; canonical: string }> = [
    { key: '<=', canonical: '<=' },
    { key: '>=', canonical: '>=' },
    { key: '==', canonical: '==' },
    { key: ' = ', canonical: ':' },
    { key: ':', canonical: ':' },
    { key: '=', canonical: ':' },
    { key: '<', canonical: '<' },
    { key: '>', canonical: '>' },
];

function parseRuleEntity(entity: string, connector?: FilterLogicalOperator): FilterRule {
    let operator = DEFAULT_FILTER_OPERATOR;
    let ruleArray: string[] = [];

    for (const op of PARSE_OPERATORS) {
        const index = entity.indexOf(op.key);
        if (index === -1) {
            continue;
        }
        const left = entity.slice(0, index).trim();
        const right = entity.slice(index + op.key.length).trim();
        if (!left || !right) {
            continue;
        }
        ruleArray = [left, right];
        operator = op.canonical;
        break;
    }

    let hasNotOperator = false;
    let attributePart = (ruleArray[0] || '').trim();
    if (attributePart.includes('NOT')) {
        attributePart = attributePart.replace('NOT', '').trim();
        hasNotOperator = true;
    }

    let keyword = (ruleArray[1] || '').trim();
    if (/\(.*\)/.test(keyword)) {
        keyword = keyword.replace(/\(|\)/g, '');
    }
    if (isDoubleQuoted(keyword)) {
        keyword = keyword.slice(1, -1);
    }

    return {
        attribute: attributePart || null,
        operator,
        keyword,
        notOperator: hasNotOperator,
        connector,
    };
}

/**
 * Saved profiles, live preview, and `?q=` can each encode the same query
 * differently (`attr:"value"`, URI-encoded `:`, or already-decoded text).
 * Undo percent-encoding so operator splitting always sees `:`.
 */
export function normalizeFilterKeyword(keyword?: string): string {
    if (!keyword) {
        return '';
    }
    const trimmed = keyword.trim();
    if (!trimmed) {
        return '';
    }
    try {
        return decodeURIComponent(trimmed);
    } catch {
        return trimmed;
    }
}

export function parseFilterQuery(keyword?: string): FilterGroup[] {
    const normalized = normalizeFilterKeyword(keyword);
    if (!normalized) {
        return createEmptyFilterGroups();
    }

    const regexForValues = new RegExp(LOGICAL_OPERATORS_STRING);
    const queryParts = splitStringByOuterParentheses(normalized);
    const groups: FilterGroup[] = [];
    let groupOperator: FilterLogicalOperator | undefined;
    let hasGroupNotOperator = false;

    queryParts.forEach((part) => {
        let queryPart = part.trim();
        if (queryPart === 'AND NOT' || queryPart === 'OR NOT') {
            groupOperator = queryPart.startsWith('AND') ? 'AND' : 'OR';
            hasGroupNotOperator = true;
            return;
        }
        if (queryPart === 'AND' || queryPart === 'OR') {
            groupOperator = queryPart;
            return;
        }

        if (queryPart.startsWith('NOT ')) {
            const afterNot = queryPart.slice(4).trim();
            if (afterNot.startsWith('(') && afterNot.endsWith(')')) {
                hasGroupNotOperator = true;
                queryPart = afterNot.substring(1, afterNot.length - 1);
            }
        }

        const group: FilterGroup = {
            rules: [],
            operator: groupOperator,
            notOperator: hasGroupNotOperator,
            ruleConnector: 'AND',
        };
        groupOperator = undefined;
        hasGroupNotOperator = false;

        const components = queryPart.split(regexForValues).filter((entry) => entry);
        let ruleOperator: FilterLogicalOperator | undefined;
        components.forEach((entity) => {
            const trimmedEntity = entity.trim();
            if (trimmedEntity === 'AND' || trimmedEntity === 'OR') {
                ruleOperator = trimmedEntity;
                return;
            }
            group.rules.push(parseRuleEntity(trimmedEntity, ruleOperator));
            ruleOperator = undefined;
        });

        if (group.rules.length > 1) {
            const firstConnector = group.rules.find((rule, index) => index > 0 && rule.connector)?.connector;
            group.ruleConnector = firstConnector ?? 'AND';
        }

        if (group.rules.length === 0) {
            group.rules.push(createEmptyRule());
        }
        groups.push(group);
    });

    return groups.length ? groups : createEmptyFilterGroups();
}

/** First non-empty keyword from stored profile, active filter, or search box. */
export function resolveFilterKeywordForEdit(
    ...candidates: Array<string | undefined | null>
): string {
    for (const candidate of candidates) {
        if (candidate && candidate.trim()) {
            return candidate;
        }
    }
    return '';
}

export function resolveRuleAttribute(
    rule: FilterRule,
    attributes: FilterAttributeOption[],
): FilterAttributeOption | null {
    const name = getAttributeName(rule.attribute);
    if (!name) {
        return null;
    }
    return attributes.find((attribute) => attribute.name === name) ?? { name };
}
